import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  setOperationStatus,
  UI_OPERATION_STATUS_REQUEST,
} from '@uncinc/react-drupal-core';

import {
  FormikWrapper,
  FormTextField as TextField,
  FormCheckbox as Checkbox,
} from '@uncinc/uncinc-react-kitchen-sink';
import useTypedDispatch from '../../../hooks/useTypedDispatch';

import usePopup from '../../../hooks/usePopup';
import { Button, Icon, Main, Popup, Table } from '../../../components';
import Page from '../../../containers/Page';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';
import { userPairWithNursePractitioner, userUnpairWithNursePractitioner } from '../../../store/actions/User';
import { getNursePractitionerInfo } from '../../../store/actions/NursePractitioner';
import useToast from '../../../hooks/useToast';

import './style.scss';

const PageAccountNursePractitioner = () => {
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();
  const { buildClass, joinClasses } = useClasses('PageAccountNursePractitioner');
  const toast = useToast();

  const pairingNursePractitionerOperationStatus = useOperationStatus('user', 'update');

  const currentUserId = useSelector((state) => state.getIn(['login', 'uid'], null));
  const userHasPoh = useSelector((state) => state.getIn(['user', currentUserId, 'field_has_poh', 0, 'value'], null));

  const nursePractitionerName = useSelector((state) => state.getIn(['nursePractitioner', 'info', 'name'], ''));
  const generalPractitioner = useSelector((state) => state.getIn(['nursePractitioner', 'info', 'general_practitioner'], ''));

  const validationSchema = Yup.object().shape({
    pairing_code: Yup.string()
      .required(t('pages.account.nurse_practitioner.fields.pairing_code.validation.required'))
      .matches(/^([a-zA-Z0-9]){6}$/, t('pages.account.nurse_practitioner.fields.pairing_code.validation.formatting')),
    check_agree: Yup.boolean()
      .isTrue(t('pages.account.nurse_practitioner.fields.checkbox_agree.validation.required'))
      .required(t('pages.account.nurse_practitioner.fields.checkbox_agree.validation.required')),
  });

  useEffect(() => {
    if (userHasPoh) {
      dispatch(getNursePractitionerInfo());
    }
  }, [userHasPoh]);

  const popup = usePopup('confirm-unpair', {
    closeOnEscape: true,
  });

  async function onPair(pairingCode) {
    await dispatch(userPairWithNursePractitioner(pairingCode))
      .then(() => {
        toast.show(t('pages.account.nurse_practitioner.pairing.success_message'), 'success');
      }).catch(() => {
        toast.show(t('pages.account.nurse_practitioner.pairing.error_message'), 'error');
      });
  }

  async function onUnpair() {
    await dispatch(userUnpairWithNursePractitioner())
      .then(() => {
        popup.close();
        toast.show(t('pages.account.nurse_practitioner.unpairing.success_message'), 'success');
      }).catch(() => {
        toast.show(t('pages.account.nurse_practitioner.unpairing.error_message'), 'error');
      });
  }

  return (
    <Page className={joinClasses(buildClass())} title={t('pages.account.nurse_practitioner.title')}>
      {popup.isOpen && (
        <Popup {...popup.popupProps}
          title={t('pages.account.nurse_practitioner.unpairing.confirmation.title')}
          cta={() => (
            <Button buttonState={pairingNursePractitionerOperationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'} onClick={onUnpair}>
              {t('pages.account.nurse_practitioner.unpairing.confirmation.confirm')}
            </Button>
          )}
        >
          {t('pages.account.nurse_practitioner.unpairing.confirmation.content')}
        </Popup>
      )}
      <Main className="col-md-8 col-lg-6 offset-md-2 offset-lg-1 py-8 py-lg-12">
        <div className={buildClass('title')}>
          <h1>{t('pages.account.nurse_practitioner.title')}</h1>
        </div>

        {(nursePractitionerName) && (
          <Table className={buildClass('nurse-practitioner-info')}>
            <Table.Row>
              <Table.Cell className={buildClass('item')}>
                <div className={buildClass('nurse-practitioner-info__title')}>
                  <h2 className={joinClasses('asH5', buildClass('name'))}>{nursePractitionerName}</h2>
                  <span>{generalPractitioner}</span>
                </div>
                <div className={buildClass('unpair')}>
                  <button type="button" className={buildClass('unpair-button')} onClick={popup.open}>
                    <Icon icon="cross" size="s" variant="1" />
                  </button>
                </div>
              </Table.Cell>
            </Table.Row>
          </Table>
        )}

        {!userHasPoh && (
          <>
            <div className={buildClass('body')}>
              <p>{t('pages.account.nurse_practitioner.body')}</p>
            </div>

            <FormikWrapper
              initialValues={{ pairing_code: '', check_agree: '' }}
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting }) => {
                onPair(values.pairing_code);
                setSubmitting(false);
              }}
              validateOnBlur={false}
              validateOnChange={false}
            >
              <TextField
                fieldName="pairing_code"
                title={t('pages.account.nurse_practitioner.fields.pairing_code.label')}
                placeholder={t('pages.account.nurse_practitioner.fields.pairing_code.placeholder')}
              />
              <Checkbox
                fieldName="check_agree"
                title={t('pages.account.nurse_practitioner.fields.checkbox_agree.label')}
                description={t('pages.account.nurse_practitioner.fields.checkbox_agree.description')}
              />
              <Button
                htmlType="submit"
                buttonState={pairingNursePractitionerOperationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
              >
                {t("pages.account.nurse_practitioner.submit")}
              </Button>
            </FormikWrapper>
          </>
        )}
      </Main>
    </Page>
  );
};

export default PageAccountNursePractitioner;
